// The "build anyway" structural disclosure (docs/phase-0/slice5-ux-flows.md §4.5;
// docs/phase-0/slice5-component-designs.md §3.1 - "visually IDENTICAL composition to
// SelfAssessmentDisclosure"). Non-dismissable, `role="note"` (a standing disclosure, not
// a live alert), composed from `.well.pad.disclosure` (never `.banner`, whose optional
// `.x` dismiss control this must never expose) - the exact same primitive
// `SelfAssessmentDisclosure.tsx` uses, mirrored here rather than imported, since that
// component is Certify's own (Slice 4) and this is a distinct Slice-5 concept ("not yet
// validated" vs. "self-assessment") that must never be visually merged with it (the ux
// spec's own "the two claims stay visually and structurally distinct" discipline,
// SelfAssessmentDisclosure.tsx:60-64's own CertificateDistinctionNote precedent).
//
// This component renders wherever the flag applies (this app's identity, wherever shown)
// - per the ux spec this is "the second structural, non-omittable disclosure in the
// product using this exact visual pattern... never independently includable from 'this
// app's identity' wherever that identity renders" (ux spec §4.5). This lane (Flows 1-2
// only) renders it on the verdict screen's own "build anyway" outcome; wiring it onto the
// Portfolio card and the AppDetail header is out of this lane's owned files and left to
// whichever lane builds those screens.
//
// Copy is DRAFT (`src/copy/validate.ts`), flagged for review - this component renders it
// verbatim and never authors or edits a word (CLAUDE.md §8/§6.6).
//
// Heading level is `<h2>` (design-review W1): this is a top-level standing note that
// sits directly under `VerdictScreen`'s own `<h1>` (VerdictScreen.tsx:136), immediately
// followed by `VerdictPackView`'s own `<h2 className="h3">` section headings - so
// h1 -> h2 -> h2 is the correct, non-skipping DOM order. `.disclosure-heading` (app.css:
// 1319-1323) is a class-only size rule with no tag selector, so the visual size is
// unchanged by the tag swap.
import { validateCopy } from "../copy/validate.js";

export const NotYetValidatedDisclosure = ({
  appName,
}: {
  readonly appName: string;
}): React.JSX.Element => (
  <div className="well pad disclosure" role="note" data-testid="not-yet-validated-disclosure">
    <div className="row aic gap8">
      <span className="disclosure-glyph" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2 4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6l-8-4Z" />
        </svg>
      </span>
      <h2 className="disclosure-heading">{validateCopy("validate.notYetValidated.heading")}</h2>
    </div>
    <p className="muted mt8" style={{ fontSize: 12.5 }}>
      {validateCopy("validate.notYetValidated.body", { appName })}
    </p>
  </div>
);
